import React, {
    useContext, useEffect, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    hideRegLoginLightbox, showRegLoginLightbox, subscribeToUserSessionStore,
} from '@zillow/user-session-store';
import {
    Anchor, Input, Checkbox, FormField, FormHelp, Label, LoadingButton, Heading, Select, Switch, Text,
} from '@zillow/constellation';

// Actions
import AddressActions from 'app/shared/actions/AddressActions';
import ModalActions from 'app/shared/actions/ModalActions';
import PostingActions from 'app/shared/actions/PostingActions';
import UserActions from 'app/shared/actions/UserActions';

// Components
import Autocomplete from 'app/shared/components/Autocomplete';

// Misc / Utils
import { AddressSelectors, UserSelectors } from 'app/shared/selectors';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import { DELIMITER } from 'app/shared/constants/AnalyticsDelimiterConstants';
import PropertyConstants from 'app/shared/constants/PropertyConstants';
import { RMWContext } from 'app/shared/providers/RMWContext';
import stringHelper from 'app/shared/utils/stringHelper';
import { useLocationRestriction, useValidateForm } from './hooks';

// Styles
import * as S from './styles';

const {
    PROPERTY_TYPES: {
        COMMUNITY,
        CONDO,
        HOUSE,
        TOWNHOUSE,
    },
    LISTING_TYPES: { RENTAL, ROOM },
} = PropertyConstants;
const CREATE_LISTING_CTA = `CreateListing${DELIMITER}List`;

const PostingSearchModule = () => {
    // redux hooks
    const dispatch = useDispatch();
    const addressList = useSelector(state => AddressSelectors.makeAddressList(state));
    const isLoggedIn = useSelector(state => UserSelectors.makeIsLoggedIn(state));

    // react hooks
    const { paths } = useContext(RMWContext);
    const [address, updateAddress] = useState('');
    const [errorObj, updateErrorObj] = useState({
        address: '',
        propertyType: '',
        numberOfUnits: '',
    });
    const [unit, updateUnit] = useState('');
    const [numUnits, updateNumUnits] = useState('');
    const [userSelectedPropertyType, updateUserSelectedPropertyType] = useState('');
    const [isRoomForRent, updateIsRoomForRent] = useState(false);
    const [duplicateAccountListingId, updateDuplicateAccountListingId] = useState('');
    const [isLoading, updateIsLoading] = useState(false);
    const [hasAttemptedCreate, updateHasAttemptedCreate] = useState(false);

    // custom hooks
    const { checkLocationRestriction, propertyTypeFromApi } = useLocationRestriction({
        isLoggedIn,
        updateDuplicateAccountListingId,
        updateErrorObj,
        updateIsLoading,
    });
    const { checkIsFormComplete, verifyAddress } = useValidateForm(updateErrorObj);

    let propertyType = userSelectedPropertyType || propertyTypeFromApi;
    if (propertyTypeFromApi === COMMUNITY) {
        propertyType = COMMUNITY;
    }
    const isCommunityType = propertyType === COMMUNITY;
    const isCondoType = propertyType === CONDO;

    // must use useRef to read the current value in the listener (handleLoginSuccess)
    // (https://stackoverflow.com/questions/55265255/react-usestate-hook-event-handler-using-initial-state)
    const addressRef = useRef();
    const unitRef = useRef();
    const numUnitsRef = useRef();
    const isRoomForRentRef = useRef();
    const propertyTypeRef = useRef();
    const duplicateAccountListingIdRef = useRef();
    const isCommunityTypeRef = useRef();
    const hasAttemptedCreateRef = useRef();
    addressRef.current = address;
    unitRef.current = unit;
    numUnitsRef.current = numUnits;
    isRoomForRentRef.current = isRoomForRent;
    propertyTypeRef.current = propertyType;
    duplicateAccountListingIdRef.current = duplicateAccountListingId;
    isCommunityTypeRef.current = isCommunityType;
    hasAttemptedCreateRef.current = hasAttemptedCreate;

    const handleLoginSuccess = async () => {
        updateIsLoading(true);
        dispatch(UserActions.updateUserInfo({
            loggedIn: true,
        }));

        // after authentication, check restrictions again to check if user has existing listing at entered address
        const hasNoRestrictions = await checkLocationRestriction({
            address: addressRef.current,
            unit: unitRef.current,
            isRoomForRent: isRoomForRentRef.current,
            propertyType: propertyTypeRef.current,
            hasAuthCookies: true,
        });

        if (hasNoRestrictions) {
            // eslint-disable-next-line no-use-before-define
            createListing();
        }
    };

    /**
     * This listener is called every time reg-login mounts or unmounts (including when the login modal
     * is opened via header link). This listener will also be called twice after authentication (succesfully
     * logging in + closing modal) - so update hasAttemptedCreate to avoid creating twice.
     */
    const handleUserSessionStoreChange = ({ user }) => {
        if (user && user.loggedIn && !hasAttemptedCreateRef.current) {
            updateHasAttemptedCreate(true);
            handleLoginSuccess();
        }
    };

    useEffect(() => {
        const unsubscribeFromUserSessionStore = subscribeToUserSessionStore(handleUserSessionStoreChange);

        return () => {
            hideRegLoginLightbox();
            unsubscribeFromUserSessionStore();
        };
    }, []);

    // action handlers
    const handleAddressChange = (value) => {
        updateAddress(value);

        // address api requires 7 characters
        if (value.length < 7) {
            return;
        }

        dispatch(AddressActions.autocomplete(value));
    };

    const selectAddress = (selectedAddress) => {
        const { name } = selectedAddress;
        updateAddress(name);

        dispatch(AddressActions.autocomplete(selectedAddress));

        checkLocationRestriction({
            address: name, unit, isRoomForRent, propertyType,
        });
    };

    const handlePropertyTypeSelect = (e) => {
        const selectedPropertyType = e.target.value;
        updateUserSelectedPropertyType(selectedPropertyType);

        checkLocationRestriction({
            address, unit, isRoomForRent, propertyType: selectedPropertyType,
        });
    };

    const handleUnitChange = (e) => {
        const enteredUnit = e.target.value;

        updateUnit(enteredUnit);
        verifyAddress({ address, unit: enteredUnit, errorObj });

        checkLocationRestriction({
            address, unit: enteredUnit, isRoomForRent, propertyType,
        });
    };

    const handleNumUnitsChange = (e) => {
        const enteredNumUnits = e.target.value;

        updateNumUnits(enteredNumUnits);
    };

    const toggleSharedListing = () => {
        updateIsRoomForRent(!isRoomForRent);

        checkLocationRestriction({
            address, unit, isRoomForRent: !isRoomForRent, propertyType,
        });
    };

    const createListing = async () => {
        updateIsLoading(true);

        // Duplicate listing exists. Redirect to existing listing rather than create a new one
        if (duplicateAccountListingIdRef.current && !isRoomForRentRef.current) {
            const gaEvent = {
                ...AnalyticsConstants.GA_EVENTS.EXISTING_LISTING_REDIRECT,
            };

            dispatch(analyticsUtils.track.GAEventV2(gaEvent));
            window.router.transitionTo(
                `${paths.propertiesPage}/${duplicateAccountListingIdRef.current}/listing`,
            );
            return;
        }

        const listingType = !isCommunityTypeRef.current && isRoomForRentRef.current ? ROOM : RENTAL;

        try {
            AnalyticsConstants.GA_EVENTS.ADD_PROPERTY_MODAL_CTA.action = CREATE_LISTING_CTA;

            const { propertyId } = await dispatch(PostingActions.property.create({
                listingType,
                propertyType: propertyTypeRef.current,
                fullAddress: addressRef.current,
                ...(!isCommunityTypeRef.current && { unitNumber: unitRef.current }),
                ...(isCommunityTypeRef.current && { numUnits: numUnitsRef.current }),
            }));

            window.dataLayer.push({ event: `RentalManager${DELIMITER}AddProperty` });

            updateIsLoading(false);

            dispatch(analyticsUtils.track.GAEventV2({
                ...AnalyticsConstants.GA_EVENTS.ADD_PROPERTY_MODAL_CTA,
            }));

            // ?intent=listing - will hide sub nav in pdp
            const redirectUrl = `${paths.propertiesPage}/${propertyId}/listing?intent=listing`;
            window.router.transitionTo(redirectUrl);
        } catch (err) {
            updateErrorObj({ ...errorObj, address: err.message });
            const formattedListingType = stringHelper.startCaseRemoveSpace(listingType);
            dispatch(
                analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.HPPP_NEW_LISTING_ERROR, {
                    action: ['listingCreatedError', formattedListingType, propertyType].join(DELIMITER),
                }),
            );
            updateIsLoading(false);
        }
    };

    const handleSubmit = async () => {
        updateIsLoading(true);

        const isFormComplete = checkIsFormComplete({
            address, propertyType, numUnits, errorObj,
        });
        if (!isFormComplete) {
            updateIsLoading(false);
            return;
        }

        const isValidAddress = await verifyAddress({ address, unit, errorObj });
        if (!isValidAddress) {
            updateIsLoading(false);
            return;
        }

        dispatch(ModalActions.updateShouldReloadOnLoginSuccess(false));

        if (isLoggedIn) {
            createListing();
        } else {
            updateIsLoading(false);
            showRegLoginLightbox();
        }
    };

    const handleSignInClick = () => {
        dispatch(ModalActions.updateShouldReloadOnLoginSuccess(true));

        dispatch(analyticsUtils.track.GAEventV2({
            category: `call_to_action${DELIMITER}text`,
            label: 'ClickThrough',
            action: `SignIn${DELIMITER}hero`,
        }));

        showRegLoginLightbox();
    };

    return (
        <S.Container>
            <S.HeadingContainer>
                <Heading as="h1" level="3" fontFamily="serif" marginBottom="xs">
                It&apos;s quick and easy to post a rental listing
                </Heading>
                <Text fontType="body" as="p">
                    <strong>
                    Fill your vacancy on the network with over 1 million daily visitors.
                        <br />
                    Post your first listing free today.
                    </strong>
                </Text>
            </S.HeadingContainer>

            <S.Row>
                <S.AddressInputContainer>
                    <FormField error={!!errorObj.address}>
                        <Label>Street address</Label>
                        <Autocomplete
                            items={addressList}
                            onChange={handleAddressChange}
                            onSelect={selectAddress}
                            placeholder="Address"
                            value={address}
                        />
                    </FormField>
                    <FormHelp error={!!errorObj.address}>{errorObj.address}</FormHelp>
                </S.AddressInputContainer>

                {isCondoType && (
                    <S.UnitInputContainer>
                        <FormField>
                            <Label>Unit</Label>
                            <Input
                                onChange={handleUnitChange}
                                value={unit}
                            />
                        </FormField>
                    </S.UnitInputContainer>
                )}
                {isCommunityType && (
                    <S.UnitInputContainer>
                        <FormField error={!!errorObj.numberOfUnits}>
                            <Label>Number of units</Label>
                            <Input
                                onChange={handleNumUnitsChange}
                                value={numUnits}
                            />
                            <FormHelp error={!!errorObj.numberOfUnits}>{errorObj.numberOfUnits}</FormHelp>
                        </FormField>
                    </S.UnitInputContainer>
                )}
            </S.Row>

            <S.PropertyTypeContainer>
                <FormField paddingY={1.5} error={!!errorObj.propertyType}>
                    <Label>Property type</Label>
                    <Select
                        aria-label="Property type"
                        value={propertyType || 'Property type'}
                        onChange={handlePropertyTypeSelect}
                        style={{ color: propertyType ? 'black' : '#A7A6AB' }}
                    >
                        <option disabled={true}>Property type</option>
                        <option disabled={propertyTypeFromApi === COMMUNITY} value={HOUSE}>{HOUSE}</option>
                        <option disabled={propertyTypeFromApi === COMMUNITY} value={CONDO}>{CONDO}</option>
                        <option disabled={propertyTypeFromApi === COMMUNITY} value={TOWNHOUSE}>{TOWNHOUSE}</option>
                        <option value={COMMUNITY}>{COMMUNITY}</option>
                    </Select>
                    <FormHelp error={!!errorObj.propertyType}>{errorObj.propertyType}</FormHelp>
                </FormField>
            </S.PropertyTypeContainer>

            {!isCommunityType && (
                <S.SharedPropertyContainer>
                    <Text fontType="body" marginRight="sm">
                        This is a room for rent with shared living space
                    </Text>
                    <Switch
                        size="md"
                        checkbox={(
                            <Checkbox
                                aria-label="Is room for rent"
                                checked={isRoomForRent}
                                onChange={() => {}}
                            />
                        )}
                        onClick={toggleSharedListing}
                    />
                </S.SharedPropertyContainer>
            )}

            <LoadingButton loading={isLoading} buttonType="primary" onClick={handleSubmit}>
                Continue
            </LoadingButton>


            {!isLoggedIn && (
                <Text fontType="bodySmall" as="p" marginTop="sm">
                Already have an account?
                    {' '}
                    <Anchor onClick={handleSignInClick}>
                    Sign in
                    </Anchor>
                </Text>
            )}
        </S.Container>
    );
};

export default PostingSearchModule;
